import { Countries } from '../../core/util/msisdn';
import { realIframe } from '../../platform/iframe/realIframe';
import rememberMeStorage from '../clients/remember-me-storage';
import * as saa from './storageAccess';
import * as cookie from './crossDomainCookie';
import { isCrossDomainRememberMeEnabled } from './featureFlags';
import { type PersistResult, type RetrievalPhase, type RetrievedIdentity } from './types';

const NONE: RetrievedIdentity = { source: 'none' };

const validate = (country?: string, number?: string): { country?: Countries; number?: string } => {
  const validCountry =
    country !== undefined && Object.values(Countries).includes(country as Countries) ?
      (country as Countries)
    : undefined;
  const validNumber = number !== undefined && /^\d+$/.test(number) ? number : undefined;
  return { country: validCountry, number: validNumber };
};

const hasValue = (id: { country?: Countries; number?: string }): boolean =>
  Boolean(id.number) || Boolean(id.country);

/** Mirror a cross-domain value down into partitioned localStorage so the next load is gesture-free. */
const mirrorToLocal = (country?: Countries, number?: string) => {
  if (country && number) {
    rememberMeStorage.set(country, number);
  }
};

export interface RetrieveOptions {
  phase: RetrievalPhase;
}

/**
 * Layered read: partitioned localStorage (instant) → unpartitioned `.vipps.no` cookie via the
 * Storage Access API → empty. The cross-domain cookie hop needs a user gesture, so it only runs in
 * the 'gesture' phase (or 'eager' when access was already granted in this page).
 */
export const retrieveIdentity = async (options: RetrieveOptions): Promise<RetrievedIdentity> => {
  const { phase } = options;

  // First-party (not embedded) — behave exactly like today.
  if (!realIframe.isEmbedded()) {
    const local = rememberMeStorage.get();
    return hasValue(local) ? { ...local, source: 'local' } : NONE;
  }

  if (!isCrossDomainRememberMeEnabled()) {
    return NONE;
  }

  // Same-merchant fast path: partitioned localStorage needs no gesture.
  const local = rememberMeStorage.get();
  if (hasValue(local)) {
    return { ...local, source: 'local' };
  }

  // Eager phase (on load, no user gesture): read the cookie directly. `document.cookie` is gesture-free
  // and never throws — it returns the `.vipps.no` cookie whenever the browser already exposes
  // unpartitioned cookies to this frame (Chrome with an active grant, first-party, or Firefox's
  // automatic interaction heuristics for returning Vipps users), and `NONE` otherwise. We never call
  // requestStorageAccess() here — that needs a user gesture and errors in Firefox/Safari.
  if (phase === 'eager') {
    return readCookie();
  }

  // Gesture phase: request access (must be inside the user gesture), then read the cookie.
  const granted = await saa.requestAccess();
  if (!granted) {
    return { ...NONE, accessRequested: true, accessGranted: false };
  }
  return { ...readCookie(), accessRequested: true, accessGranted: true };
};

const readCookie = (): RetrievedIdentity => {
  const raw = cookie.read();
  const validated = validate(raw?.country, raw?.number);
  if (!hasValue(validated)) {
    return NONE;
  }
  mirrorToLocal(validated.country, validated.number);
  return { ...validated, source: 'storage-access' };
};

/**
 * Layered write. First-party writes localStorage as today. Embedded writes localStorage (same-merchant
 * best effort) and, when storage access is granted, the cross-domain cookie. Submit is itself a gesture,
 * so requesting access here is allowed. Never throws into the payment flow.
 */
export const persistIdentity = async (
  country: Countries,
  number: string
): Promise<PersistResult> => {
  try {
    rememberMeStorage.set(country, number);

    if (!realIframe.isEmbedded() || !isCrossDomainRememberMeEnabled()) {
      return { durable: false };
    }

    const granted = await saa.requestAccess();
    if (!granted) {
      return { durable: false };
    }
    cookie.write({ country, number });
    return { durable: true };
  } catch {
    return { durable: false };
  }
};

/**
 * Acquire the cross-domain storage-access grant from within a user gesture (e.g. on the submit
 * button's real click, before any delay), so a later `persistIdentity()` can write the `.vipps.no`
 * cookie without a gesture-less request. No-op unless embedded + enabled; safe to fire-and-forget.
 */
export const primeStorageAccess = (): void => {
  if (realIframe.isEmbedded() && isCrossDomainRememberMeEnabled()) {
    void saa.requestAccess();
  }
};

export const clearIdentity = async (): Promise<void> => {
  try {
    rememberMeStorage.clear();
    if (!realIframe.isEmbedded() || !isCrossDomainRememberMeEnabled()) {
      return;
    }
    if (await saa.requestAccess()) {
      cookie.clear();
    }
  } catch {
    // best-effort
  }
};
